import { faCog } from "@fortawesome/pro-regular-svg-icons";
import { faArrowToRight, faCaretDown, faCaretUp, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { useTranslation } from "react-i18next";
import { Grid, Icon, Modal, Table } from "semantic-ui-react";

import { ActionButtons, ConditionIcon, Expandable, GalleryButton } from "../../../../../components";
import { Responsive } from "../../../../../layout";
import { ANSWERS } from "../../../../../util/answers";
import { PIN_TYPE, QUESTION_RESULT_STATUS, getImageAndVideoFileTypes, getInterventionPriceWithVAT, getServicePricePlaceholder, renderInterventionElementType, round2 } from "../../../../../util/common";

import "./index.scss";

const handleUpdateIntervention = (intervention_id, answer, readOnly, pendingInterventions, onUpdatePendingInterventions) => {
  if (readOnly) return;

  const updatedInterventions = pendingInterventions.map(i => (i.id === intervention_id ? { ...i, communication_result_status: answer } : i));

  onUpdatePendingInterventions(updatedInterventions);
};

const ScheduledServices = ({
  settings,
  showMobile,
  cartOnly,
  readOnly,
  highlightInterventions,
  approvedInterventions,
  pendingInterventions,
  onUpdatePendingInterventions,
}) => {
  const { t } = useTranslation();
  const [isApprovedOpen, setIsApprovedOpen] = React.useState(false);
  const [selectedIntervention, setSelectedIntervention] = React.useState(null);

  const { include_vat, vat, display_intervention_elements } = settings;

  const renderPrice = intervention => {
    const { include_vat, vat } = settings;

    const pricePlaceholder = getServicePricePlaceholder(intervention, settings, t);
    if (pricePlaceholder) return <span className="scheduled-services-price-placeholder">{pricePlaceholder}</span>;

    if (!(intervention.price > 0.01)) return null;

    const price = include_vat ? getInterventionPriceWithVAT(intervention, { include_vat, vat }) : intervention.price;

    return <span className="scheduled-services-price">€ {round2(price).toFixed(2)}</span>;
  };

  const isNotFixButtonVisible = ({ question_result_status, pin_type_id }) => {
    if (settings.cannot_decline_warranty_types && pin_type_id === PIN_TYPE.WARRANTY) return false;

    if (isNaN(question_result_status)) return settings.enable_not_fix_without_status;

    switch (question_result_status) {
      case QUESTION_RESULT_STATUS.NECESSARY:
        return settings.enable_not_fixed_critical;
      case QUESTION_RESULT_STATUS.ADVISED:
        return settings.enable_not_fixed_advised;
      default:
        return true;
    }
  };

  const renderIntervention = (intervention, index, highlightInterventions, readOnly, cartOnly, isPendingIntervention = false) => {
    const { images, videos } = getImageAndVideoFileTypes(intervention.attachments);

    const isInterventionRequired =
      highlightInterventions &&
      !intervention.customer_ok &&
      intervention.communication_result_status === ANSWERS.NOT_ANSWERED &&
      (intervention.question_result_status === QUESTION_RESULT_STATUS.NECESSARY ||
        (intervention.question_result_status === QUESTION_RESULT_STATUS.ADVISED && !settings.skip_optional_items) ||
        (intervention.question_result_status === undefined && !settings.skip_without_status_items));

    return (
      <Grid.Row className={`scheduled-services-item ${isInterventionRequired ? "highlight" : ""}`} key={index}>
        <Grid.Column computer={5} widescreen={6} mobile={16}>
          <div className="scheduled-services-row-title">
            <ConditionIcon className="scheduled-services-condition-icon" status={intervention.question_result_status} />
            {display_intervention_elements && intervention.elements.length > 0 && (
              <Icon name="info circle" color="black" onClick={() => setSelectedIntervention(intervention)} />
            )}
            <Expandable text={intervention.title} />
          </div>
        </Grid.Column>
        <Grid.Column computer={isPendingIntervention ? 4 : 6} mobile={16}>
          <div className="scheduled-services-row-remark -margin-bottom-mobile-10">
            <Expandable text={intervention.description} />
          </div>
        </Grid.Column>
        <Grid.Column className={`scheduled-services-price-gallery ${!isPendingIntervention ? "approved-intervention" : ""}`} computer={3} mobile={16}>
          {renderPrice(intervention, include_vat, vat)}
          <GalleryButton images={images} videos={videos} />
        </Grid.Column>
        {isPendingIntervention && (
          <Grid.Column computer={4} widescreen={3} mobile={16} className="actions-buttons-container">
            <ActionButtons
              status={intervention.communication_result_status}
              showNotFixedButton={isNotFixButtonVisible(intervention)}
              onAnswer={answer => handleUpdateIntervention(intervention.id, answer, readOnly, pendingInterventions, onUpdatePendingInterventions)}
              readOnly={readOnly || intervention.readOnly}
              cartOnly={cartOnly}
            />
          </Grid.Column>
        )}
      </Grid.Row>
    );
  };

  const renderApprovedInterventions = () => {
    if (!approvedInterventions?.length) return null;

    return (
      <>
        <Grid.Row className="scheduled-services-header">
          <div className="scheduled-services-header-button" onClick={() => setIsApprovedOpen(!isApprovedOpen)}>
            <span className="scheduled-services-header-button-title">{t("cc_approved").message || "Approved"}</span>
            <FontAwesomeIcon icon={isApprovedOpen ? faCaretUp : faCaretDown} className="scheduled-services-header-button-icon" />
          </div>
        </Grid.Row>

        {isApprovedOpen && approvedInterventions.map((intervention, index) => renderIntervention(intervention, index, highlightInterventions, readOnly, cartOnly))}
      </>
    );
  };

  const renderPendingInterventions = () => {
    if (!pendingInterventions?.length) return null;

    return (
      <>
        <Grid.Row className="scheduled-services-header">
          <div className="scheduled-services-header-button">
            <span className="scheduled-services-header-button-title">{t("cc_approval_required").message || "Approval required"}</span>
          </div>
        </Grid.Row>
        {pendingInterventions.map((intervention, index) => renderIntervention(intervention, index, highlightInterventions, readOnly, cartOnly, true))}
      </>
    );
  };

  const renderInterventionElementsModal = () => {
    return (
      <Modal open={!!selectedIntervention} className="scheduled-services-elements-modal" size="small">
        <Modal.Header>
          <div className="scheduled-services-elements-title">{selectedIntervention?.title}</div>
          <FontAwesomeIcon icon={faTimes} onClick={() => setSelectedIntervention(null)} />
        </Modal.Header>
        <Modal.Content>
          <Table>
            <Table.Body>
              {selectedIntervention?.elements.map(e => (
                <Table.Row key={e.id}>
                  <Table.Cell>
                    {e.parent_dms_nr && (
                      <div className="intervention-element-arrow-to-right">
                        <FontAwesomeIcon icon={faArrowToRight} />
                      </div>
                    )}
                    {renderInterventionElementType(e.element_type)}
                    <div className="intervention-element-description">{e.description}</div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Modal.Content>
      </Modal>
    );
  };

  if (!approvedInterventions.length && !pendingInterventions.length) return null;

  return (
    <Responsive showMobile={showMobile}>
      <Grid className="v2-scheduled-services-container component-container">
        <Grid.Row>
          <div className="scheduled-services-header">
            <div className="scheduled-services-header-title">
              <FontAwesomeIcon icon={faCog} className="scheduled-services-header-icon" />
              <h4>{t("cc_services").message || "Services"}</h4>
            </div>
          </div>
        </Grid.Row>
        {renderApprovedInterventions()}
        {renderPendingInterventions()}
      </Grid>
      {renderInterventionElementsModal()}
    </Responsive>
  );
};

const filterApprovedInterventions = interventions => {
  return interventions.filter(i => i.customer_ok);
};

const filterPendingInterventions = interventions => {
  return interventions.filter(i => !i.customer_ok);
};

export default ScheduledServices;
export { filterApprovedInterventions, filterPendingInterventions };
